import Icon from '../../Icon';
import classnames from 'classnames';
import { useEffect, useState, useRef } from 'react';
import DropdownSelect from '../../DropdownSelect';
import TagButton from '../../Buttons/TagButton';
import { OrderLineViewModel } from '@/@types/client-api';
import { MyCartLabels } from '../labels';

interface QuantityInputProps {
  item: OrderLineViewModel;
  currentQuantity: string;
  labels: ReturnType<typeof MyCartLabels>;
  onQuantityChange: (
    item: OrderLineViewModel,
    newQuantity: string,
    shouldUpdateCartItem: boolean
  ) => void;
}

const QuantityInput = ({ item, currentQuantity, labels, onQuantityChange }: QuantityInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [justSwitchedToInput, setJustSwitchedToInput] = useState(false);
  const useDropdown = currentQuantity !== '' && Number(currentQuantity) < 5;
  const minQuantity = 0;
  const isOverMaxQuantity =
    item?.maxQuantity &&
    !isNaN(Number(currentQuantity)) &&
    Number(currentQuantity) > item.maxQuantity;
  const isNotANumber = currentQuantity !== '' && isNaN(Number(currentQuantity));
  const infoEmail = import.meta.env.VITE_APP_INFO_EMAIL;

  const quantityOptions = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5+', value: '5+' }
  ];

  useEffect(() => {
    if (justSwitchedToInput && !useDropdown && inputRef.current) {
      inputRef.current.focus();
      setJustSwitchedToInput(false);
    }
  }, [justSwitchedToInput, useDropdown]);

  const handleQuantityChange = (newQuantity: string) => {
    if (newQuantity === '5+') {
      newQuantity = '';
      setJustSwitchedToInput(true);
    } else if (newQuantity === '' && Number(newQuantity) < minQuantity) {
      newQuantity = '1';
    }

    const isValidNumber = !isNaN(Number(newQuantity)) && newQuantity !== '';
    const isOverMaxQuantity =
      item?.maxQuantity && !isNaN(Number(newQuantity)) && Number(newQuantity) > item.maxQuantity;
    const shouldUpdateCartItem = isValidNumber && !isOverMaxQuantity;

    onQuantityChange(item, newQuantity, shouldUpdateCartItem);
  };

  return (
    <>
      {useDropdown ? (
        <DropdownSelect
          align="start"
          value={currentQuantity}
          onValueChange={value => handleQuantityChange(value)}
          options={quantityOptions}
          placeholder="Qty"
          button={({ isOpen, value }) => {
            return (
              <TagButton
                isActive={isOpen}
                icon={isOpen ? 'chevron-up' : 'chevron-down'}
                styleType="solid"
                label={value || currentQuantity}
                className={classnames({
                  '!border-transparent !bg-gray-lightest !text-black': isOpen
                })}
              />
            );
          }}
        />
      ) : (
        <>
          <div className="relative w-20 rounded-lg border border-gray bg-gray-feather px-4 pb-3 pt-2 transition-colors hover:border-black">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label
                  htmlFor={`quantity-${item.code!}`}
                  className='text-2-fixed-medium text-gray-dark transition-opacity after:pointer-events-none after:absolute after:inset-[-2px] after:rounded-lg after:border-2 after:border-black after:opacity-0 after:content-[""]'
                >
                  Quantity
                </label>
                <input
                  ref={inputRef}
                  id={`quantity-${item.code!}`}
                  className="text-input z-10 w-full cursor-text bg-gray-feather placeholder:text-gray focus:outline-none"
                  placeholder="Qty"
                  type="text"
                  min="1"
                  aria-label="Qty"
                  aria-describedby={`quantity-error-number-${item.code!}`}
                  aria-details={`quantity-error-max-${item.code!}`}
                  defaultValue={currentQuantity}
                  onBlur={e => handleQuantityChange(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleQuantityChange(e.currentTarget.value);
                      e.currentTarget.blur();
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div
            id={`quantity-error-max-${item.code!}`}
            className={classnames('text-2-fixed mt-1 flex gap-1 text-red', {
              hidden: !isOverMaxQuantity
            })}
          >
            <Icon name={'alert'} size="small" />
            Contact us at{' '}
            <a href={`mailto:${infoEmail}`} className="text-red underline hover:text-red-dark">
              {infoEmail}
            </a>{' '}
            if you wish to purchase {item.maxQuantity! + 1} or more.
          </div>
          <div
            id={`quantity-error-number-${item.code!}`}
            className={classnames('text-2-fixed mt-1 flex gap-1 text-red', {
              hidden: !isNotANumber
            })}
            role="alert"
            aria-live="polite"
          >
            <Icon name={'alert'} size="small" />
            {labels.invalidQuantityText}
          </div>
        </>
      )}
    </>
  );
};

export default QuantityInput;
